<template>
  <div class="schedule">
    <v-progress-linear v-if="isLoading" :indeterminate="true" :height="2"></v-progress-linear>
    <div v-if="schedules">
      <win-information v-if="winInformation" :winInformation="winInformation"/>
      <v-toolbar dense>
        <v-spacer></v-spacer>
        <v-icon color="primary" border>mdi-filter</v-icon>
        <v-btn-toggle
          style="margin-right:-8px"
          v-model="matchFilter"
          dense
          color="primary"
          mandatory
        >
          <v-btn :value="MATCH_FILTER_OWN">{{$t('schedule.filter.my-matches')}}</v-btn>
          <v-btn :value="MATCH_FILTER_ALL">{{$t('schedule.filter.all-matches')}}</v-btn>
        </v-btn-toggle>
      </v-toolbar>
      <v-toolbar dense v-if="gameModeIsSingleplayer(game.mode)">
        <v-spacer></v-spacer>
        <v-checkbox
          hide-details="auto"
          v-model="showOnlineId"
          :label="$t('schedule.show-online-id')"
        ></v-checkbox>
      </v-toolbar>
      <div v-if="notes && notes.length > 0" class="pa-2">
        <notes-widget :notes="notes"/>
      </div>

      <v-expansion-panels multiple v-model="openPanels">
        <v-expansion-panel v-for="schedule in schedules" :key="schedule.uid">
          <v-expansion-panel-header ripple class="pa-2 headline">{{schedule.name}}</v-expansion-panel-header>
          <v-expansion-panel-content
            class="match"
            border
            v-for="match in filterMatches(schedule.matches)"
            :key="match.uid"
          >
            <placeholder-schedule-match v-if="match.type == 'placeholder'" :uid="match.uid" :game="game" :text="match.text"/>
            <schedule-match
              v-else
              :showIdOfPlatform="showOnlineId ? game.platform : undefined"
              :uid="match.uid"
              :schedule="schedule"
              :player="getParticipantFromGameByUid(game, match.player.uid, match.player)"
              :opponent="getParticipantFromGameByUid(game, match.opponent.uid, match.opponent)"
              :result="match.result"
              :single-games="match.result ? match.result.singleGames : null"
              :match-date="match.matchDate"
              :editable="match.editable"
              :disabled="!(scheduleMatchUIDEditing === undefined || scheduleMatchUIDEditing == match.uid)"
              :game="game"
              :loading="match._loading"
              :forceDialog="true"
              v-on:userInput="onScheduleMatchUserInput"
              v-on:matchClicked="onMatchClicked(schedule, match)"
            ></schedule-match>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
    <game-match-dialog
      v-if="editMatchDialogVisible"
      :visible="editMatchDialogVisible"
      :uid="editMatch.uid"
      :single-games="editMatch.result ? editMatch.result.singleGames : null"
      :chat-channel="editMatch.chatChannel"
      :closeAction="function() {editMatchDialogVisible = false}"
      :result="editMatch.result"
      :player="editMatchAdditional.player"
      :opponent="editMatchAdditional.opponent"
      :game="game"
      :editable="editMatch.editable"
      :match-date="editMatchAdditional.matchDate"
    />
  </div>
</template>

<script>
import {
  EVENT_SCHEDULES_AVAILABLE,
  EVENT_GAME_RESULT_AVAILABLE,
  EVENT_PUSHER_EVENT_RECEIVED,
  EVENT_REFRESH_DATA
} from "@/plugins/eventbus";
import ScheduleMatch from "@/widgets/ScheduleMatch";
import PlaceholderScheduleMatch from "@/widgets/PlaceholderScheduleMatch";
import GameMatchDialog from "@/widgets/GameMatchDialog";
import WinInformation from "@/widgets/WinInformation";
import NotesWidget from './NotesWidget.vue';
import competitionMixin from "@/mixins/competitionMixin"
import gameModeMixin from "@/mixins/gameModeMixin"

const MATCH_FILTER_OWN = "own";
const MATCH_FILTER_ALL = "all";

export default {
  name: "Schedule",
  components: { ScheduleMatch, PlaceholderScheduleMatch, GameMatchDialog, WinInformation, NotesWidget },
  mixins: [competitionMixin, gameModeMixin],
  props: {
    game: {
      type: Object,
      required: true
    }
  },
  data: function() {
    return {
      isLoading: false,
      addResultLoading: false,
      winInformation: undefined,
      schedules: undefined,
      notes: undefined,
      matchFilter: MATCH_FILTER_ALL,
      scheduleMatchUIDEditing: undefined,
      editMatchDialogVisible: false,
      editMatch: undefined,
      editMatchAdditional: {},
      openMatchAfterSchedulesLoaded: false,
      showOnlineId: false,
      openPanels: []
    };
  },
  watch: {
    game: function(newVal, oldVal) {
      if (newVal.uid != oldVal.uid) {
        this.refreshSchedule();
      }
    }
  },
  mounted() {
    this.refreshSchedule()
    if (this.game.game.startsWith('fifa-pro-clubs') && this.gameModeIsSingleplayer(this.game.mode)) {
      this.showOnlineId = true
    }
    this.$eventBus.$on(EVENT_REFRESH_DATA, this.onRefreshData);
    this.$eventBus.$on(EVENT_SCHEDULES_AVAILABLE, this.onSchedulesAvailable);
    this.$eventBus.$on(EVENT_GAME_RESULT_AVAILABLE, this.gameResultAvailable);
    this.$eventBus.$on(EVENT_PUSHER_EVENT_RECEIVED, this.onPusherEvent);
    if (this.$route.query.match != undefined) {
      this.openMatchAfterSchedulesLoaded = this.$route.query.match;
      console.log('openAfterSchedulesLoaded', this.openMatchAfterSchedulesLoaded);
    }
  },
  created() {
    this.MATCH_FILTER_OWN = MATCH_FILTER_OWN;
    this.MATCH_FILTER_ALL = MATCH_FILTER_ALL;
  },
  beforeDestroy() {
    this.$eventBus.$off(EVENT_PUSHER_EVENT_RECEIVED, this.onPusherEvent);
    this.$eventBus.$off(EVENT_GAME_RESULT_AVAILABLE, this.gameResultAvailable);
    this.$eventBus.$off(EVENT_SCHEDULES_AVAILABLE, this.onSchedulesAvailable);
    this.$eventBus.$off(EVENT_REFRESH_DATA, this.onRefreshData);
  },
  methods: {
    onScheduleMatchUserInput(data) {
      // console.log(data)
      if (data.any) {
        this.scheduleMatchUIDEditing = data.uid;
      } else {
        this.scheduleMatchUIDEditing = undefined;
      }
    },
    gameResultAvailable(event) {
      if (event.gameuid == this.game.uid) {
        let match = this.findMatchByUid(event.matchUid);
        if (match) {
          match.result = event.result;
        }
      }
    },
    onSchedulesAvailable(event) {
      if (event.gameuid == this.game.uid) {
        this.overtakeSchedules(event.schedules)
      }
    },
    reset() {
      this.schedules = undefined;
    },
    onRefreshData() {
      this.refreshSchedule();
    },
    refreshSchedule() {
      this.isLoading = true;
      if (this.game.type == "ko.classic") {
        this.$api
          .loadKOGameSchedules(this.game.uid)
          .then(response => {
            this.isLoading = false;
            this.overtakeSchedules(response.data);
          })
          .catch(error => {
            this.isLoading = false;
            this.$common.onError(error)
          });
      } else if (this.game.type == "ko.mixed") {
        this.$api
            .loadKoMixedGameSchedules(this.game.uid)
            .then(response => {
              this.isLoading = false;
              this.overtakeSchedules(response.data);
            })
            .catch(error => {
              this.isLoading = false;
              this.$common.onError(error)
            });
      } else {
        this.$api
          .loadLigaGameSchedules(this.game.uid)
          .then(response => {
            this.isLoading = false;
            this.overtakeSchedules(response.data);
          })
          .catch(error => {
            this.isLoading = false;
            this.$common.onError(error)
          });
      }
    },
    overtakeSchedules(data) {
      // console.log(data)
      let isFirstLoad = (this.schedules ? false : true)
      var openedScheduleUids = undefined
      if (!isFirstLoad && this.openPanels) {
        openedScheduleUids = []
        this.openPanels.forEach((idx) => {
          openedScheduleUids.push(this.schedules[idx].uid)
        })
        // console.log(openedScheduleUids)
      }
      let schedules = [];
      data.gameRounds.forEach(gameRound => {
        // console.log('GameRound', gameRound);
        gameRound.matches.forEach(match => {
          // TODO: Handle multiple schedules, not only the first!
          match._loading = false;
        });
        schedules.push({ uid: gameRound.uid, name: gameRound.name, overwritable: gameRound.overwritable, matches: gameRound.matches, allResultsAvailable: gameRound.allResultsAvailable });
      });
      this.schedules = schedules.reverse();
      this.notes = data.notes;
      if (isFirstLoad) {
        // set the expanded (open) panels (game rounds)
        let panelsExpanded = [];
        for (var i=0;i<schedules.length; i++) {
          if (schedules[i].allResultsAvailable === false) {
            panelsExpanded.push(i)
          }
        }
        this.openPanels = panelsExpanded;
      } else {
        // Update the open panels to the previous opened tabs
        let idxs = []
        openedScheduleUids.forEach(uid => {
          let scheduleIdx = this.schedules.findIndex((s) => {
            return s.uid == uid
          });
          if (scheduleIdx != -1) {
            idxs.push(scheduleIdx)
          }
        })
        // console.log(openedScheduleUids, 'new', idxs)
        this.openPanels = idxs;
      }
      this.winInformation = data.winInformation;
      if (this.openMatchAfterSchedulesLoaded) {
        console.log('openMatch dialog now!', this.openMatchAfterSchedulesLoaded)
        this.openMatchDialogByUid(this.openMatchAfterSchedulesLoaded)
        this.openMatchAfterSchedulesLoaded = undefined;
      }
    },
    filterMatches(matches) {
      let user = this.$store.state.profile ? this.$store.state.profile.name : null;
      switch (this.matchFilter) {
        case MATCH_FILTER_ALL:
          return matches;
        case MATCH_FILTER_OWN:
          return matches.filter(
              m => (m.player?.teamPlayers &&
                  (m.player?.teamPlayers?.filter(tp => tp.player.name === user).length > 0 ||
                  m.opponent?.teamPlayers?.filter(tp => tp.player.name === user).length > 0)) ||
                  (m.player && m.player.name === user) ||
                  (m.opponent && m.opponent.name === user)
          );
        default:
          return matches;
      }
    },
    onPusherEvent(e) {
      if (!this.schedules) {
        return;
      }
      if (e.type == "Client\\MatchResultAdded") {
        if (e.event.gameUid == this.game.uid) {
          let match = this.findMatchByUid(e.event.match.uid);
          if (match) {
            match.result = e.event.match.result;
          }
        }
      }
    },
    findMatchByUid(matchUid) {
      var ret = undefined
      this.schedules.forEach((schedule) => {
        schedule.matches.forEach((match) => {
          if (match.uid == matchUid) {
            ret = match;
          }
        })
      })
      return ret
    },
    onMatchClicked(schedule, match) {
      // console.log('schedule', schedule, 'match', match);
      this.openMatchDialog(match)
    },
    openMatchDialogByUid(matchUid) {
      var showMatch = undefined;
      this.schedules.forEach((schedule) => {
        let match = schedule.matches.find((m) => {
          return m.uid == matchUid;
        });
        if (match) {
          showMatch = match;
        }
      })
      if (showMatch) {
        this.openMatchDialog(showMatch)
      } else {
        console.log(`Match ${matchUid} not found`)
      }
    },
    openMatchDialog(match) {
      this.editMatch = match
      this.editMatchAdditional = {
        player: this.getParticipantFromGameByUid(this.game, match.player.uid, match.player),
        opponent: this.getParticipantFromGameByUid(this.game, match.opponent.uid, match.opponent),
        matchDate: match.matchDate
      }
      this.editMatchDialogVisible = true;
    },
  }
};
</script>

<style scoped>
.winner {
  color: var(--v-primary-base);
  text-shadow: 0 0 5px var(--v-primary-darken1), 0 0 10px var(--v-primary-darken1),
    0 0 30px var(--v-primary-darken1), 0 0 35px var(--v-primary-darken1);
}
</style>

<style>
.match .v-expansion-panel-content__wrap {
  padding: 0 12px 16px 8px !important;
}
</style>
